import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Adsense } from '@ctrl/react-adsense';
import { RampUnit } from "@playwire/pw-react-component";
import { useLocation } from 'react-router-dom';
import { DndContext, pointerWithin, rectIntersection, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { StyledContainer, StyledAdsContainer } from './styled';
import GameFields from './components/GameFields 3.0';
import {
  isStrategyLoading,
  getMyMajorSquad,
  getCurrentStrategy,
  getCurrentMatchRunning,
  hasMatchEngine25,
} from '../../../../redux/selector/Team';

import {
  teamGetMyStrategy,
  removePlayerFromStaff,
} from '../../../../redux/actions/Team';
// import { adsClickRequest } from '../../../../redux/actions/ads';
import { allowTour } from '../../../../redux/actions/TourControl';
import { getTourState } from '../../../../redux/selector/TourControl';
import { isPremiumUser, getMyCurrentTeam } from '../../../../redux/selector/User';
import { shouldAutoStartTour, tourStorageValue } from '../../../../helpers/tour/locationToStorage';
import { isMatchLive } from '../../../../service/liveMatch';
import { isSentOff } from '../../../../service/matchEngine';
import StrategySquad from './components/StrategySquad';
import { Players } from './layout';
import StrategyDragOverlay from './components/StrategyDragOverlay';
import MobileSquad from './components/MobileSquad';
import useLiveMatchPoll from '../../../../hooks/useLiveMatchPoll';
import useLiveStrategyRefresh from '../../../../hooks/useLiveStrategyRefresh';
import useNarrowPane from '../../../../hooks/useNarrowPane';
import { PITCH_ASPECT } from './positionStyle';
import { StrategyPlayerMenuProvider } from './playerMenuContext';

export default function StrategyContainer() {
  const [activeDraggingId, setActiveDraggingId] = React.useState('');
  const layoutRef = React.useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint:{distance:5}}), useSensor(KeyboardSensor));
  React.useLayoutEffect(() => {
    const element = layoutRef.current;
    let frame = 0;
    const resize = () => {
      if (!element || window.innerWidth < 1200) return;
      const pitch = element.querySelector('.football-container');
      if (!pitch) return;
      let scroller = element.parentElement;
      while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
      const bottom = scroller ? scroller.getBoundingClientRect().bottom - parseFloat(getComputedStyle(scroller).paddingBottom || 0) : window.innerHeight;
      // Both values are viewport coordinates. Adding scrollTop here makes the
      // workspace shrink every time the page is scrolled.
      const top = element.getBoundingClientRect().top;
      const availableWidth = element.clientWidth - 24 - 210 - 390;
      const headerHeight = element.querySelector('header')?.offsetHeight || 40;
      // Do not let wrapping fixture content feed back into the pitch size. A
      // smaller pitch makes those cards narrower, which otherwise makes them
      // wrap even more and collapse the whole desktop workspace.
      const measuredFixtureHeight = element.querySelector('[data-strategy-fixtures]')?.offsetHeight || 120;
      const fixtureHeight = Math.min(160, Math.max(100, measuredFixtureHeight));
      const reservedHeight = headerHeight + fixtureHeight + 20;
      // Prefer growing taller (closer to the field art) over stretching wider.
      const height = Math.max(240, Math.min(bottom - top - reservedHeight, availableWidth / PITCH_ASPECT));
      element.style.setProperty('--pitch-height', `${height}px`);
      element.style.setProperty('--workspace-body-height', `${height + fixtureHeight + 12}px`);
      element.style.setProperty('--pitch-scale', Math.min(1, height / 600, pitch.clientWidth / 750));
    };
    // Defer style writes so ResizeObserver does not re-enter in the same frame
    // ("ResizeObserver loop completed with undelivered notifications").
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(resize);
    };
    const observer = new ResizeObserver(schedule);
    if (element) observer.observe(element);
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, []);
  const location = useLocation();
  const dispatch = useDispatch();
  const isPremium = useSelector(isPremiumUser);
  const isLoad = useSelector(isStrategyLoading);
  const MyTeam = useSelector(getMyMajorSquad);
  const MyStrategy = useSelector(getCurrentStrategy);
  const isTourStart = useSelector(getTourState);
  const currentMatch = useSelector(getCurrentMatchRunning);
  const teamInfo = useSelector(getMyCurrentTeam);
  const userTeamId = teamInfo?._id || teamInfo?.id;
  const isNarrowPane = useNarrowPane();
  const isMatchEngine25 = useSelector(hasMatchEngine25);
  useLiveMatchPoll(currentMatch);
  useLiveStrategyRefresh(currentMatch, isMatchEngine25);

  const removePlayerFromSquad = (id) => {
    if (isSentOff(id, currentMatch, userTeamId)) return;
    dispatch(removePlayerFromStaff(id));
  };

  useEffect(() => {
    dispatch(teamGetMyStrategy());
  }, [dispatch]);

  // While a match is live, refetch strategy on focus so sent-off / cards appear
  // without waiting for pull-to-refresh (and so a 403 can be explained).
  useEffect(() => {
    if (!isMatchLive(currentMatch)) return undefined;
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        dispatch(teamGetMyStrategy());
      }
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [dispatch, currentMatch]);
  const onDragStart = ({ active }) => {
    setActiveDraggingId(active.id);
  };
  const onDragEnd = ({ active, over }) => {
    // Only an explicit drop on the player list removes a pitch player.
    // Missing a target or pressing Escape must leave the line-up intact.
    const activeId = String(active.id).split(' ')[0];
    if (
      over?.id === 'playersList' &&
      MyTeam.some((player) => player._id === activeId)
    ) {
      removePlayerFromSquad(activeId);
    }
    setActiveDraggingId(null);
  };

  useEffect(() => {
    const activeLocalStorageTourState = localStorage.getItem(
      tourStorageValue[location.pathname]
    );
    if (
      !isNarrowPane &&
      !isLoad &&
      !isTourStart &&
      !activeLocalStorageTourState &&
      shouldAutoStartTour()
    ) {
      dispatch(allowTour(true));
    }
  }, [isLoad, isTourStart, dispatch, location.pathname, isNarrowPane]);

  /*   useEffect(() => {
    const handleAdsClick = () => {
      if (document.activeElement === document.querySelector('iframe')) {
        dispatch(adsClickRequest());
      }
    };

    window.addEventListener('blur', handleAdsClick);
    return () => {
      window.removeEventListener('blur', handleAdsClick);
    };
  }, []); */


  return (
    <StrategyPlayerMenuProvider>
      <DndContext
        sensors={sensors}
        collisionDetection={(args) => {
          const hits = args.pointerCoordinates ? pointerWithin(args) : rectIntersection(args);
          // Prefer a specific list row over its containing bench drop zone.
          return hits.sort((a,b)=>Number(/^list-(slot|bench):/.test(String(b.id)))-Number(/^list-(slot|bench):/.test(String(a.id))));
        }}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveDraggingId(null)}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <StyledContainer ref={layoutRef}>
            <GameFields
              MyStrategy={MyStrategy}
              MyTeam={MyTeam}
              activeDraggingId={activeDraggingId}
            />
            {isNarrowPane ? <MobileSquad/> : <Players><StrategySquad loading={isLoad}/></Players>}
          </StyledContainer>
{/*           {!isPremium && (<RampUnit
          type="dfp-med-rect"
          cssClass="leaderboard"
        />)} */}
{/*           {!isPremium && (
            <StyledAdsContainer>
              <Adsense
                client={`${!isPremium ? 'ca-pub-2739224896366063' : ''}`}
                slot={`${!isPremium ? '9437045662' : ''}`}
                style={{
                  width: '100%',
                  height: `${!isPremium ? 300 : 0}`,
                  display: `${!isPremium ? 'flex' : 'none'}`,
                  flexDirection: 'center',
                  marginTop: '20px',
                }}
                layout="in-article"
                format="fluid"
              />
            </StyledAdsContainer>
          )} */}
        </div>
        <StrategyDragOverlay/>
      </DndContext>
    </StrategyPlayerMenuProvider>
  );
}
